import { useState, useEffect } from 'react'
import { Loader2, Plus, Trash2, RefreshCw, Search, Lock } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { useAuthContext } from '../contexts/AuthContext'
import userManagementService, { CreateUserRequest } from '../services/userManagementService'
import clientService, { Client } from '../services/clientService'
import localUserService, { LocalUserInput } from '../services/localUserService'

interface User {
  username: string
  email: string
  cognitoGroup: 'superAdmin' | 'groupAdmin' | 'groupUser'
  clientId: string
  clientName?: string
  status: 'active' | 'inactive'
  createdAt?: string
}

export default function UserManagement() {
  const { hasAnyGroup } = useAuthContext()
  const [users, setUsers] = useState<User[]>([])
  const [clients, setClients] = useState<Client[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [createDialogOpen, setCreateDialogOpen] = useState(false)
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [userToDelete, setUserToDelete] = useState<User | null>(null)
  const [deleting, setDeleting] = useState(false)
  const [creating, setCreating] = useState(false)
  
  const hasPermission = hasAnyGroup(['superAdmin', 'groupAdmin'])
  
  const [searchQuery, setSearchQuery] = useState('')
  const [groupFilter, setGroupFilter] = useState<string>('all')
  
  const [newEmail, setNewEmail] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [newCognitoGroup, setNewCognitoGroup] = useState<'superAdmin' | 'groupAdmin' | 'groupUser'>('groupUser')
  const [newClientId, setNewClientId] = useState('')
  
  const [formErrors, setFormErrors] = useState<{
    email?: string
    password?: string
    clientId?: string
  }>({})

  const loadUsers = async () => {
    try {
      setLoading(true)
      setError(null)
      
      const clientsData = await clientService.listClients()
      setClients(clientsData)
      
      // Fetch users from Cognito (superAdmin and groupAdmin)
      const cognitoUsers = await userManagementService.listUsers()
      
      // Map Cognito users to our User interface
      const mappedCognitoUsers: User[] = cognitoUsers.map(user => {
        const client = clientsData.find(c => c.id === user.parentGroup)
        
        return {
          username: user.username,
          email: user.email,
          cognitoGroup: user.cognitoGroup as 'superAdmin' | 'groupAdmin' | 'groupUser',
          clientId: user.parentGroup,
          clientName: client?.name,
          status: user.enabled && user.status === 'CONFIRMED' ? 'active' : 'inactive',
          createdAt: user.createdAt
        }
      })
      
      // Fetch ALL LocalUsers from DynamoDB (groupUser) - fetch once for all clients
      const allLocalUsers: User[] = []
      const seenEmails = new Set<string>() // Deduplicate by email
      
      for (const client of clientsData) {
        try {
          const localUsers = await localUserService.listLocalUsers(client.id)
          
          for (const user of localUsers) {
            // Skip if we've already seen this email
            if (seenEmails.has(user.email)) {
              console.warn(`Duplicate LocalUser found: ${user.email}`)
              continue
            }
            
            seenEmails.add(user.email)
            
            allLocalUsers.push({
              username: user.username,
              email: user.email,
              cognitoGroup: 'groupUser',
              clientId: user.clientId,
              clientName: clientsData.find(c => c.id === user.clientId)?.name,
              status: user.isActive ? 'active' : 'inactive',
              createdAt: user.createdAt
            })
          }
        } catch (err) {
          console.warn(`Failed to fetch local users for client ${client.id}:`, err)
        }
      }
      
      console.log(`Loaded ${mappedCognitoUsers.length} Cognito users and ${allLocalUsers.length} LocalUsers`)
      
      // Combine Cognito users and LocalUsers
      setUsers([...mappedCognitoUsers, ...allLocalUsers])
    } catch (err) {
      console.error('Error loading users:', err)
      setError('Failed to load users. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    userManagementService.initialize().then(() => {
      loadUsers()
    }).catch(err => {
      console.error('Failed to initialize user management service:', err)
      setError('Failed to initialize user management service.')
      setLoading(false)
    })
  }, [])

  const handleCreateClick = () => {
    setNewEmail('')
    setNewPassword('')
    setNewCognitoGroup('groupUser')
    setNewClientId('')
    setFormErrors({})
    setCreateDialogOpen(true)
  }

  const handleCreateClose = () => {
    setCreateDialogOpen(false)
    setFormErrors({})
  }

  const validateForm = (): boolean => {
    const errors: typeof formErrors = {}
    
    if (!newEmail.trim()) {
      errors.email = 'Email is required'
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(newEmail)) {
      errors.email = 'Invalid email format'
    }
    
    if (!newPassword) {
      errors.password = 'Password is required'
    } else if (newPassword.length < 8) {
      errors.password = 'Password must be at least 8 characters'
    } else if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(newPassword)) {
      errors.password = 'Password must contain uppercase, lowercase, and number'
    }
    
    if (!newClientId.trim()) {
      errors.clientId = 'Client is required'
    }
    
    setFormErrors(errors)
    return Object.keys(errors).length === 0
  }

  const handleCreateUser = async () => {
    if (!validateForm()) {
      return
    }

    try {
      setCreating(true)
      setError(null)
      
      const selectedClient = clients.find(c => c.id === newClientId)
      
      // If groupUser, create as LocalUser in DynamoDB (NOT Cognito)
      if (newCognitoGroup === 'groupUser') {
        console.log('Creating LocalUser (not Cognito) for groupUser role')
        
        const localUserInput: LocalUserInput = {
          username: newEmail.split('@')[0], // Use email prefix as username
          email: newEmail.trim(),
          password: newPassword,
          clientId: newClientId.trim(),
          role: 'viewer', // Default role for groupUser
        }
        
        // Get current user for createdBy field
        const currentUser = await userManagementService.getCurrentUser()
        
        const localUser = await localUserService.createLocalUser(
          localUserInput,
          currentUser?.username || 'system'
        )
        
        const newUser: User = {
          username: localUser.username,
          email: localUser.email,
          cognitoGroup: 'groupUser',
          clientId: newClientId,
          clientName: selectedClient?.name,
          status: 'active',
          createdAt: new Date().toISOString(),
        }
        
        setUsers(prev => [newUser, ...prev])
        setCreateDialogOpen(false)
        
        console.log(`LocalUser ${localUser.id} created and synced to devices`)
      } else {
        // For superAdmin and groupAdmin, create in Cognito
        console.log('Creating Cognito user for admin role')
        
        const request: CreateUserRequest = {
          username: newEmail.trim(),
          email: newEmail.trim(),
          temporaryPassword: newPassword,
          cognitoGroup: newCognitoGroup,
          parentGroup: newClientId.trim(),
        }
        
        const userId = await userManagementService.createUser(request)
        
        const newUser: User = {
          username: request.email,
          email: request.email,
          cognitoGroup: request.cognitoGroup,
          clientId: newClientId,
          clientName: selectedClient?.name,
          status: 'active',
          createdAt: new Date().toISOString(),
        }
        
        setUsers(prev => [newUser, ...prev])
        setCreateDialogOpen(false)
        
        console.log(`Cognito user ${userId} created successfully`)
      }
    } catch (err) {
      console.error('Error creating user:', err)
      setError(err instanceof Error ? err.message : 'Failed to create user. Please try again.')
    } finally {
      setCreating(false)
    }
  }

  const handleDeleteClick = (user: User) => {
    setUserToDelete(user)
    setDeleteDialogOpen(true)
  }

  const handleDeleteClose = () => {
    setDeleteDialogOpen(false)
    setUserToDelete(null)
  }

  const handleConfirmDelete = async () => {
    if (!userToDelete) return

    try {
      setDeleting(true)
      setError(null)
      
      // Delete based on user type
      if (userToDelete.cognitoGroup === 'groupUser') {
        // LocalUser - delete from DynamoDB
        // Find the LocalUser by email to get the ID
        const localUsers = await localUserService.listLocalUsers(userToDelete.clientId)
        const localUser = localUsers.find(u => u.email === userToDelete.email)
        
        if (localUser) {
          await localUserService.deleteLocalUser(localUser.id)
          console.log(`LocalUser ${localUser.id} deleted successfully`)
        } else {
          throw new Error('LocalUser not found')
        }
      } else {
        // Cognito user - delete from Cognito
        await userManagementService.deleteUser(userToDelete.username)
        console.log(`Cognito user ${userToDelete.username} deleted successfully`)
      }
      
      setUsers(prev => prev.filter(u => 
        !(u.username === userToDelete.username && u.email === userToDelete.email)
      ))
      
      setDeleteDialogOpen(false)
      setUserToDelete(null)
    } catch (err) {
      console.error('Error deleting user:', err)
      setError(err instanceof Error ? err.message : 'Failed to delete user. Please try again.')
    } finally {
      setDeleting(false)
    }
  }

  const getGroupVariant = (group: string) => {
    switch (group) {
      case 'superAdmin':
        return 'destructive'
      case 'groupAdmin':
        return 'default'
      case 'groupUser':
        return 'secondary'
      default:
        return 'outline'
    }
  }

  const getStatusVariant = (status: string) => {
    return status === 'active' ? 'default' : 'secondary'
  }

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'Unknown'
    return new Date(dateString).toLocaleString()
  }

  const filteredUsers = users.filter(user => {
    const matchesSearch = 
      user.username.toLowerCase().includes(searchQuery.toLowerCase()) ||
      user.email.toLowerCase().includes(searchQuery.toLowerCase())
    
    const matchesGroup = groupFilter === 'all' || user.cognitoGroup === groupFilter
    
    return matchesSearch && matchesGroup
  })

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    )
  }

  if (!hasPermission) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] space-y-4">
        <Lock className="h-16 w-16 text-muted-foreground" />
        <h2 className="text-2xl font-bold">Access Denied</h2>
        <p className="text-muted-foreground text-center max-w-md">
          You don't have permission to access user management.
          <br />
          Only Super Admins and Group Admins can manage users.
        </p>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-4xl font-bold">User Management</h1>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={loadUsers}
            disabled={loading}
          >
            <RefreshCw className="mr-2 h-4 w-4" />
            Refresh
          </Button>
          <Button onClick={handleCreateClick}>
            <Plus className="mr-2 h-4 w-4" />
            Create User
          </Button>
        </div>
      </div>

      {error && (
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <div className="flex gap-4 p-4 border rounded-lg">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search users..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9"
          />
        </div>
        <Select value={groupFilter} onValueChange={setGroupFilter}>
          <SelectTrigger className="w-[200px]">
            <SelectValue placeholder="Filter by Group" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Groups</SelectItem>
            <SelectItem value="superAdmin">Super Admin</SelectItem>
            <SelectItem value="groupAdmin">Group Admin</SelectItem>
            <SelectItem value="groupUser">Group User</SelectItem>
          </SelectContent>
        </Select>
      </div>
      
      <div className="border rounded-lg">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Username</TableHead>
              <TableHead>Email</TableHead>
              <TableHead>Group</TableHead>
              <TableHead>Client</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Created</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredUsers.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} className="text-center py-8">
                  <p className="text-sm text-muted-foreground">
                    {users.length === 0 
                      ? 'No users created yet. Create your first user to get started.'
                      : 'No users match your search criteria.'}
                  </p>
                </TableCell>
              </TableRow>
            ) : (
              filteredUsers.map((user) => (
                <TableRow key={`${user.email}-${user.clientId}`}>
                  <TableCell className="font-medium">{user.username}</TableCell>
                  <TableCell>{user.email}</TableCell>
                  <TableCell>
                    <Badge variant={getGroupVariant(user.cognitoGroup)}>
                      {user.cognitoGroup}
                    </Badge>
                  </TableCell>
                  <TableCell>{user.clientName || user.clientId}</TableCell>
                  <TableCell>
                    <Badge variant={getStatusVariant(user.status)}>
                      {user.status}
                    </Badge>
                  </TableCell>
                  <TableCell>{formatDate(user.createdAt)}</TableCell>
                  <TableCell className="text-right">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDeleteClick(user)}
                    >
                      <Trash2 stroke="red" className="h-4 w-4 text-destructive" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {/* Create User Dialog */}
      <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Create New User</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="email">Email *</Label>
              <Input
                id="email"
                type="email"
                autoFocus
                value={newEmail}
                onChange={(e) => setNewEmail(e.target.value)}
                className={formErrors.email ? 'border-destructive' : ''}
              />
              {formErrors.email && (
                <p className="text-sm text-destructive">{formErrors.email}</p>
              )}
              {!formErrors.email && (
                <p className="text-sm text-muted-foreground">Email will be used as the username</p>
              )}
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="password">Temporary Password *</Label>
              <Input
                id="password"
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className={formErrors.password ? 'border-destructive' : ''}
              />
              {formErrors.password && (
                <p className="text-sm text-destructive">{formErrors.password}</p>
              )}
              {!formErrors.password && (
                <p className="text-sm text-muted-foreground">
                  Min 8 chars, must include uppercase, lowercase, and number
                </p>
              )}
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="group">Cognito Group *</Label>
              <Select value={newCognitoGroup} onValueChange={(value) => setNewCognitoGroup(value as 'superAdmin' | 'groupAdmin' | 'groupUser')}>
                <SelectTrigger id="group">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="groupUser">Group User</SelectItem>
                  <SelectItem value="groupAdmin">Group Admin</SelectItem>
                  <SelectItem value="superAdmin">Super Admin</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="client">Client *</Label>
              <Select value={newClientId} onValueChange={setNewClientId}>
                <SelectTrigger id="client" className={formErrors.clientId ? 'border-destructive' : ''}>
                  <SelectValue placeholder="Select a client" />
                </SelectTrigger>
                <SelectContent>
                  {clients.length === 0 ? (
                    <SelectItem value="none" disabled>No clients available</SelectItem>
                  ) : (
                    clients.map((client) => (
                      <SelectItem key={client.id} value={client.id}>
                        {client.name}
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
              {formErrors.clientId && (
                <p className="text-sm text-destructive">{formErrors.clientId}</p>
              )}
              {!formErrors.clientId && (
                <p className="text-sm text-muted-foreground">
                  User will have access to all devices in this client
                </p>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={handleCreateClose} disabled={creating}>
              Cancel
            </Button>
            <Button onClick={handleCreateUser} disabled={creating}>
              {creating ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Creating...
                </>
              ) : (
                'Create User'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete User</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete user "{userToDelete?.username}"?
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <p className="text-sm text-muted-foreground mb-2">This will:</p>
            <ul className="list-disc list-inside text-sm text-muted-foreground space-y-1">
              <li>Remove the user from Cognito</li>
              <li>Deactivate the user on all devices</li>
              <li>Invalidate all active sessions</li>
            </ul>
            <p className="text-sm text-destructive mt-4">
              This action cannot be undone.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={handleDeleteClose} disabled={deleting}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleConfirmDelete} disabled={deleting}>
              {deleting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Deleting...
                </>
              ) : (
                'Delete User'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
